<script setup>
import "flatpickr/dist/flatpickr.css";

import { datadogLogs } from "@datadog/browser-logs";
import * as Sentry from "@sentry/vue";
import { keepPreviousData } from "@tanstack/vue-query";
import { parse } from "date-fns";
import _ from "lodash";
import { computed, onBeforeMount, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toast-notification";
import { useStore } from "vuex";
import AcademicSection from "@/components/ExperiencePage/AcademicSection/AcademicSection.vue";
import { useApplicationProgress } from "@/components/ExperiencePage/Algolia/composables.js";
import ApplicationExistsModal from "@/components/ExperiencePage/ApplicationExistsModal.vue";
import InternshipSection from "@/components/ExperiencePage/InternshipSection/InternshipSection.vue";
import ExcursionBlock from "@/components/ExperiencePage/ProgramDetailsSection/excursions/Excursion.vue";
import FinancialAid from "@/components/ExperiencePage/ProgramDetailsSection/financial-aid";
import HostInstitutionBlock from "@/components/ExperiencePage/ProgramDetailsSection/host-institution";
import HousingBlock from "@/components/ExperiencePage/ProgramDetailsSection/housing";
import LocationBlock from "@/components/ExperiencePage/ProgramDetailsSection/location";
import PriceBreakdownModal from "@/components/ExperiencePage/ProgramDetailsSection/price-breakdown";
import TeamBlock from "@/components/ExperiencePage/ProgramDetailsSection/team";
import StudentsSaying from "@/components/ExperiencePage/StudentsSection/StudentsSaying.vue";
import useExperiences from "@/components/MyExperiences/composables/useExperiences.js";
import ProgramDates from "@/components/ProgramDates";
import Ribbon from "@/components/shared/Badge/Ribbon";
import ApplyButton from "@/components/shared/Button/ApplyButton.vue";
import CloudinaryVideo from "@/components/shared/CloudinaryVideo/index.vue";
import ArrowRight from "@/components/shared/icons/ArrowRight.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import SimpleBreadCrumb from "@/components/shared/SimpleBreadCrumb.vue";
import { useDynamicHeight } from "@/composables/useDynamicHeights.js";
import { CLOUDINARY_DEFAULT_IMAGE_TRANSFORMATION, ERROR_TIMEOUT, ORDER_STATUS, REGISTER_TYPE } from "@/constants.js";
import { buildStringDateRangeSessionTravel, formatDate, parseSessionStartDate } from "@/mixins/helpers";
import { useGetProgramPage } from "@/providers/programPages/useGetProgramPage.ts";
import Page404 from "@/university/views/404Page.vue";
import { firstVideoMedia, imageTransformation, isImageMedia } from "@/util/cloudinary";
import { isFacultyLed } from "@/util/isFacultyLed";
import { pastSessionDeadline } from "@/util/pastSessionDeadline";
import { toNonEmptyTrimmedString } from "@/util/string";
import { isValidUuid } from "@/util/uuid";
import AboutSection from "../Sessions/Components/About";
import ProgramDatesSection from "../Sessions/Components/ProgramDatesSection";
import ProgramDetailSideBar from "../SideBarMenu/ProgramDetailSideBar.vue";

const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const { experiences } = useExperiences();
const { onApplyClicked, showApplicationTakenModal } = useApplicationProgress();

if (_.isEmpty(store.state.billingRules)) {
  store.dispatch("getBillingRules");
}

const configurableRegistrationFlag = computed(() => store.state?.featureFlags?.["configurable-registration"]);
const programPageId = computed(() => route?.params?.programPageId);
const isProgramPageIdValid = computed(() => isValidUuid(programPageId.value));

const routeSessionId = computed(() => {
  const raw = route?.query?.["session-id"];
  const value = Array.isArray(raw) ? raw[0] : raw;
  return toNonEmptyTrimmedString(value);
});

const sessionAll = computed(() => Boolean(route?.query?.["session-all"]));
const sessionId = ref(routeSessionId.value ?? "");
const displayedSessionId = ref("");
const sessions = ref([]);
const programDetails = ref(undefined);
const scrollTarget = ref("");
const preview = ref(false);
const leadVideo = ref(undefined);
const image = ref("");
const mediaName = computed(() => toNonEmptyTrimmedString(programDetails.value?.name) ?? "Program");
// biome-ignore lint/correctness/noUnusedVariables: bound in the template, which Biome does not see
const videoLabel = computed(() => `${mediaName.value} program video`);
const oktaId = computed(() => store.state.currentUser?.participantId ?? "");
const profileData = computed(() => store.state.profileData);

const homeInstitutionId = computed(() => store?.state?.profileData?.colleges?.[0]?.college_id ?? "");

const deadlineNotPassedSessionsSortedByClosestApplicationDeadline = computed(() => {
  const today = new Date();
  return _.chain(sessions.value)
    .filter((session) => {
      const applicationDeadline = convertStringToDate(session.application_deadline, session.id);
      return _.isDate(applicationDeadline) && applicationDeadline > today;
    })
    .sortBy((session) => convertStringToDate(session.application_deadline, session.id))
    .value();
});

const nextAvailableSessionIndex = computed(() => {
  return sessions.value.findIndex((session) => {
    return session.id === deadlineNotPassedSessionsSortedByClosestApplicationDeadline.value?.[0]?.id;
  });
});

onMounted(async () => {
  if (store.state.currentUser) await store.dispatch("getStudentApplications");

  // Get profile data if we have a profile id
  if (oktaId.value && _.isEmpty(profileData.value)) {
    await store.dispatch("getProfile", oktaId.value);
  }
});

onUnmounted(() => {
  if (observer.value) observer.value.disconnect();
});

const isValidProgramPage = ref(true);
const observer = ref(null);
const sideBarItemsToObserve = ref([]);

const initializeIntersectionObserver = () => {
  if (!observer.value) {
    // initialize observer if it hasn't started
    // https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
    observer.value = new IntersectionObserver(
      (entries) => {
        entries.forEach((entryRecord) => {
          if (entryRecord?.target?.id) {
            const foundElement = sideBarItemsToObserve.value.find((item) => item.id === entryRecord?.target?.id);
            if (foundElement) {
              foundElement.isIntersecting = entryRecord.isIntersecting;
              foundElement.intersectionRatio = entryRecord.intersectionRatio;
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: [0, 0.25, 0.5, 0.7, 0.8, 0.9, 1],
      },
    );
  }
};

const retryObservation = (elementId) => {
  if (!observer.value) {
    return;
  }
  const maxRetries = 60;
  let retries = 0;
  const retryInterval = setInterval(() => {
    if (retries >= maxRetries) {
      clearInterval(retryInterval);
      return;
    }
    const element = document.getElementById(elementId);
    if (element) {
      try {
        observer.value.observe(element);
        clearInterval(retryInterval);
      } catch (e) {
        clearInterval(retryInterval);
      }
    }
    retries++;
  }, 1000);
};

const addElementToObserver = (elementId) => {
  if (!observer.value) {
    return;
  }
  const element = document.getElementById(elementId);
  if (!element) {
    retryObservation(elementId);
    return;
  }
  try {
    observer.value.observe(element);
  } catch (e) {
    console.warn(`Failed to observe item: ${elementId} retrying...`, e);
    retryObservation(elementId);
  }
};

const setActiveMenuItems = (menuItems) => {
  // Stores which elements on screen user can interact with
  sideBarItemsToObserve.value = menuItems.map((item) => {
    return {
      isIntersecting: false,
      intersectionRatio: 0,
      id: item.links, // id of section
      sidebarid: item.sidebarlink, // id of sidebar element
    };
  });

  // Sets up observer to track which sections are currently being viewed
  initializeIntersectionObserver();

  // Observe menu items
  menuItems.forEach((menuItem) => {
    addElementToObserver(menuItem.links);
  });
};

const sideBarIntersectingElements = computed(() => {
  return structuredClone(sideBarItemsToObserve.value).sort((a, b) => b.intersectionRatio - a.intersectionRatio); // doesn't modify og array
});

watch(
  () => sideBarIntersectingElements.value,
  (recentElements) => {
    for (let i = 0; i < recentElements.length; i++) {
      const sideBarElementId = recentElements[i].sidebarid;
      const sideBarElement = document.getElementById(sideBarElementId);
      if (sideBarElement) {
        // First item in array will be highlighted
        i === 0 && recentElements[i].isIntersecting
          ? sideBarElement.classList.add("bg-gray-experienceitem")
          : sideBarElement.classList.remove("bg-gray-experienceitem");
      }
    }
  },
);

const scrollToSection = (id) => {
  scrollTarget.value = document.getElementById(id);
  if (scrollTarget.value) {
    scrollTarget.value.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }
};

const isMobile = () => {
  return window.innerWidth <= 760;
};

const isNotMobile = () => {
  return window.innerWidth >= 760;
};

const domainEntityId = computed(() => {
  return store.state?.domainEntityId;
});

onBeforeMount(async () => {
  if (!isValidUuid(programPageId.value)) {
    datadogLogs.logger.warn("invalid_program_page_id_rejected", {
      event_name: "invalid_program_page_id_rejected",
      program_page_id_raw: String(programPageId.value),
    });
    isValidProgramPage.value = false;
    return;
  }

  try {
    await fetchAndUseProgramPageData();
  } catch (error) {
    isValidProgramPage.value = false;
  }
});

const fetchAndUseProgramPageData = async () => {
  executeFetchProgramPageOptions();
};

const {
  data: programPageOptions,
  refetch: executeFetchProgramPageOptions,
  isLoading,
  isPlaceholderData,
  error,
} = useGetProgramPage(programPageId, homeInstitutionId, domainEntityId, sessionAll, sessionId, {
  enabled: isProgramPageIdValid,
  refetchOnWindowFocus: false,
  placeholderData: keepPreviousData,
});

watch(error, (err) => {
  if (!err) {
    return;
  }

  datadogLogs.logger.error("program_page_load_error", {
    event_name: "program_page_load_error",
    program_page_id: String(programPageId.value),
    error_message: err.message,
  });

  const status = err?.response?.status;
  if (status === 404 || status === 403) {
    isValidProgramPage.value = false;
  }
});

function applySessionsSortByStartDate(session1, session2) {
  const session1StartDate = parseSessionStartDate(session1?.session_travel_details);
  const session2StartDate = parseSessionStartDate(session2?.session_travel_details);
  return session1StartDate - session2StartDate;
}

/**
 * Converts a String to a Date object
 * @param {String} dateString -> a date in the following format: yyyy-mm-dd
 * @returns {Date} A date object
 */
function convertStringToDate(dateString = "", sessionId = "") {
  try {
    return parse(dateString, "yyyy-MM-dd", new Date());
  } catch (e) {
    Sentry.captureException(e, {
      tags: {
        troubleshooting_note: `Error while trying to convert string into a date object for session: ${sessionId}`,
      },
    });
  }
}

watch(programPageOptions, (state) => {
  if (isPlaceholderData.value) {
    return;
  }

  const programData = state?.data?.data?.items ?? {};
  programDetails.value = programData;
  const leadMedia = JSON.parse(programData?.lead_media ?? "[]");
  leadVideo.value = firstVideoMedia(leadMedia);
  const leadImage = Array.isArray(leadMedia) ? leadMedia.find((item) => isImageMedia(item)) : undefined;
  image.value = leadImage
    ? imageTransformation({
        ...CLOUDINARY_DEFAULT_IMAGE_TRANSFORMATION,
        path: leadImage,
        height: 900,
        width: 1600,
      })
    : undefined;
  if (Array.isArray(programData.sessions)) {
    sessions.value = [...programData.sessions].sort((session1, session2) =>
      applySessionsSortByStartDate(session1, session2),
    );

    const currentSelectedId = toNonEmptyTrimmedString(sessionId.value);
    const candidateIds = [currentSelectedId, routeSessionId.value].filter(Boolean);
    const matchedId = candidateIds.find((id) => sessions.value.some((s) => s?.id === id));

    if (matchedId) {
      if (matchedId !== sessionId.value) {
        sessionId.value = matchedId;
      }
    } else {
      let ind;
      if (routeSessionId.value) {
        ind = 0;
      } else {
        ind = nextAvailableSessionIndex.value < 0 ? 0 : nextAvailableSessionIndex.value;
      }
      const fallbackId = sessions.value?.[ind]?.id;
      if (fallbackId && fallbackId !== sessionId.value) {
        sessionId.value = fallbackId;
      }
    }

    // Only advance the displayed session once we have confirmed data for it.
    // This prevents selectedSession from collapsing to {} while keepPreviousData
    // is serving the old response during a session switch.
    displayedSessionId.value = sessionId.value;
  }
});

const session_profiles = computed(() => {
  if (!Array.isArray(sessions.value) || !displayedSessionId.value) return [];
  return sessions.value
    .filter((session) => session.id === displayedSessionId.value)
    .flatMap((session) => session.session_profiles);
});

const selectedSession = computed(() => {
  return sessions.value?.find((session) => session?.id === displayedSessionId.value) || {};
});

const setSelectedSession = async (selectedSessionId) => {
  const currentSelectedSessionId = route.query["session-id"] || null;
  if (currentSelectedSessionId !== selectedSessionId) {
    sessionId.value = selectedSessionId;
    // Update session id param
    await router.replace({
      name: "program-detail-view",
      params: {
        programPageId: programPageId.value,
      },
      query: {
        ...(route?.query ?? {}),
        "session-id": selectedSessionId,
      },
    });
  }
};

const selectedSessionHostInstitution = computed(() =>
  selectedSession.value?.session_hosting_institutions &&
  Array.isArray(selectedSession.value?.session_hosting_institutions)
    ? selectedSession?.value?.session_hosting_institutions?.[0]?.entity
    : {},
);

const selectedSessionCity = computed(() =>
  selectedSession.value?.session_travel_details && Array.isArray(selectedSession.value?.session_travel_details)
    ? selectedSession.value?.session_travel_details?.[0]?.arrival_city
    : {},
);

const selectedSessionInternships = computed(() =>
  selectedSession.value?.internship_filter_criteria ? selectedSession.value?.internship_filter_criteria : {},
);

const getLearnerQuotes = computed(() =>
  programDetails.value?.program_page_testimonials && Array.isArray(programDetails.value?.program_page_testimonials)
    ? programDetails.value?.program_page_testimonials
    : [],
);

watch(programPageId, async (newValue) => {
  if (!isValidUuid(newValue)) {
    datadogLogs.logger.warn("invalid_program_page_id_rejected", {
      event_name: "invalid_program_page_id_rejected",
      program_page_id_raw: String(newValue),
    });
    isValidProgramPage.value = false;
    return;
  }

  isValidProgramPage.value = true;
  try {
    await fetchAndUseProgramPageData();
  } catch (error) {
    isValidProgramPage.value = false;
    toast.open({
      message: `There was a problem loading the information for this progam.  Program reference id: ${newValue}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
});

const closeModal = () => {
  preview.value = !preview.value;
};

const observeSection = (elementId) => {
  // Elements removed/deleted from DOM need to be added to observer again.
  // id must match the id of the section here
  addElementToObserver(elementId);
};

const menuInfoSection = ref(null);
const menuItemsSection = ref(null);
const menuSection = ref(null);
const sections = [menuInfoSection, menuItemsSection];
const { inViewHeight } = useDynamicHeight(sections, menuSection, 40);
// Function to parse the description and extract text
const parseDescriptionAndExtractText = (description) => {
  if (!description) return "";

  let json;

  try {
    json = JSON.parse(description);
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        troubleshooting_note: "Error parsing program page description.",
      },
    });
    return "";
  }

  // Recursive function to extract text from the parsed JSON structure
  const extractText = (node) => {
    if (!node || !node.content) return "";

    return node.content.map((child) => (child.type === "text" ? child.text : extractText(child))).join(" ");
  };

  return extractText(json); // Call the extractText function with the parsed JSON
};

// Computed property that uses the extraction function
const extractedText = computed(() => {
  return parseDescriptionAndExtractText(programDetails.value?.description);
});

// Computed property that checks if the extracted text is non-empty
const hasAboutText = computed(() => {
  return !!extractedText.value.trim();
});

const externalLink = computed(() => {
  if (selectedSession.value?.registration_type === "external_listing") {
    if (selectedSession.value?.external_link_url.startsWith("https")) {
      return selectedSession.value?.external_link_url;
    }
    return `https://${selectedSession.value?.external_link_url}`;
  }
  return "";
});

const registerType = computed(() => {
  if (!configurableRegistrationFlag.value) {
    return REGISTER_TYPE.PERSONALIZE_APPLY;
  }
  const existingExperience = experiences.value?.find(
    (experience) => experience.session_id === selectedSession.value?.id,
  );
  if (existingExperience) {
    if (existingExperience.order_status === ORDER_STATUS.draft) {
      return selectedSession.value?.requires_application
        ? REGISTER_TYPE.CONTINUE_APPLICATION
        : REGISTER_TYPE.CONTINUE_REGISTRATION;
    } else if (
      [ORDER_STATUS.submitted, ORDER_STATUS.withdrawn, ORDER_STATUS.fulfilled, ORDER_STATUS.paidConfirmed].includes(
        existingExperience.order_status,
      )
    ) {
      return REGISTER_TYPE.CONTINUE_ENROLLMENT;
    }
  } else if (selectedSession.value?.is_personalized) {
    if (selectedSession.value?.requires_application) return REGISTER_TYPE.PERSONALIZE_APPLY;
    return REGISTER_TYPE.PERSONALIZE;
  } else if (selectedSession.value?.requires_application) {
    return REGISTER_TYPE.APPLY;
  } else if (selectedSession.value?.registration_type === "register") {
    return REGISTER_TYPE.REGISTER;
  } else if (selectedSession.value?.registration_type === "external_listing") {
    return selectedSession.value.external_link_site_name ?? REGISTER_TYPE.EXTERNAL_LINK;
  }
  return "Select";
});

const bannerDates = computed(() =>
  buildStringDateRangeSessionTravel(
    selectedSession.value?.session_travel_details,
    {
      academic_start_date: selectedSession.value?.academic_start_date,
      academic_end_date: selectedSession.value?.academic_end_date,
    },
    "long",
  ),
);

// Read off the first travel-detail row, null or absent — counts as not final.
const isStartDateFinal = computed(() =>
  Boolean(selectedSession.value?.session_travel_details?.[0]?.arrival_date_is_final),
);
const isEndDateFinal = computed(() =>
  Boolean(selectedSession.value?.session_travel_details?.[0]?.departure_date_is_final),
);

const viewMoreDeadlinesText = computed(() => {
  if (registerType.value === REGISTER_TYPE.PERSONALIZE || registerType.value === REGISTER_TYPE.REGISTER)
    return "View More Registration Deadlines";
  return "View More Application Deadlines";
});
</script>

<template>
  <div class="md:flex md:flex-col">
    <ApplicationExistsModal
      :show-application-taken-modal="showApplicationTakenModal"
      @close="showApplicationTakenModal = false"
    />
    <PriceBreakdownModal
      v-if="programDetails"
      :session="selectedSession"
      :program="programDetails"
      :open-modal="preview"
      @close-modal="closeModal"
    ></PriceBreakdownModal>
    <SimpleBreadCrumb
      class="md:!ml-[85px] !ml-0 md:mt-[12px] md:mb-[10px]"
      :custom-active-class="
        `${secondaryColor}` ? `${secondaryColorClass} font-semibold` : `text-university-secondary font-semibold`
      "
    />
    <div v-if="isValidProgramPage">
      <!--- CONTENT ---->
      <div
        class="grid max-w-2xl grid-cols-1 grid-rows-1 items-start gap-x-8 gap-t-8 lg:mx-0 sm:max-w-none md:grid-cols-2 md:gap-x-0"
      >
        <div class="mt-4 sm:mx-0 lg:row-end-1 lg:col-span-1 lg:row-span-1 px-4 md:px-0 md:pl-24 lg:pl-24">
          <SkeletonLoader v-if="isLoading" :height="'h-10'" rounded />
          <p
            v-if="selectedSessionCity && selectedSession?.session_travel_details && !isLoading"
            class="text-base font-bold text-indigo-base"
            id="program-location"
          >
            {{ selectedSessionCity.city_ascii }},
            {{ selectedSessionCity.country.name }}
          </p>
          <SkeletonLoader v-if="isLoading" :height="'h-16'" rounded />
          <h1 v-if="!isLoading" class="mb-2 capitalize font-bold text-4xl tracking-wide leading-none text-indigo-base" aria-describedby="program-location">
            {{ programDetails?.name ?? "" }}
          </h1>
          <div class="flex mb-2 items-center space-x-3">
            <div
              v-if="selectedSession && selectedSession.session_travel_details && !isLoading"
              class="flex items-center"
            >
              <img src="/images/icon-calendar.svg" loading="lazy" class="h-6 w-6" alt="Icon Calendar" />
              <ProgramDates
                :id="`banner-program-dates-${displayedSessionId}`"
                :dates="bannerDates"
                :start-is-final="isStartDateFinal"
                :end-is-final="isEndDateFinal"
                tag="div"
                class="ml-3"
                reverse-icon-order
                experience-card-icon
                text-class="md:whitespace-nowrap text-md font-semibold text-gray-700"
              />
            </div>
            <SkeletonLoader v-if="isLoading" :height="'h-7'" class="mb-1 mt-2" rounded />
            <div v-else>
              <div v-if="isNotMobile()" class="flex-shrink-0">
                <div class="lg:flex justify-left items-center w-full flex-wrap">
                  <button
                    class="font-bold text-base uppercase flex flex-row items-center cursor-pointer"
                    :class="[secondaryColor ? `${secondaryColorClass}` : `text-university-secondary`]"
                    @click="scrollToSection('dates')"
                  >
                    View more session dates
                    <ArrowRight custom-class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <SkeletonLoader v-if="isLoading && isMobile()" :height="'h-7'" class="mb-1 mt-2" rounded />
          <div v-else>
            <div v-if="isMobile()" class="flex mb-2 items-center space-x-3">
              <div class="flex-shrink-0">
                <div class="lg:flex justify-left items-center w-full flex-wrap">
                  <button
                    class="font-bold text-base uppercase text-university-secondary flex flex-row items-center cursor-pointer"
                    :class="[secondaryColor ? `${secondaryColorClass}` : `text-university-secondary`]"
                    @click="scrollToSection('dates')"
                  >
                    View more session dates
                    <ArrowRight custom-class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <SkeletonLoader v-if="isLoading" :height="'h-7'" class="mb-1 mt-2" rounded />
          <div v-else class="flex mb-2 items-center space-x-3">
            <div class="flex items-center">
              <img src="/images/icon_calendar_clock.svg" class="h-6 w-6" alt="Clock inside calendar icon" />
              <p class="flex ml-3 whitespace-nowrap text-md font-semibold text-gray-700">
                {{ formatDate(selectedSession?.application_deadline, "long") }}</p
              >
            </div>
            <div v-if="isNotMobile()" class="flex-shrink-0">
              <div class="lg:flex justify-left items-center w-full flex-wrap">
                <button
                  class="font-bold text-base uppercase flex flex-row items-center cursor-pointer"
                  :class="[secondaryColor ? `${secondaryColorClass}` : `text-university-secondary`]"
                  @click="scrollToSection('dates')"
                >
                  {{ viewMoreDeadlinesText }}
                  <ArrowRight custom-class="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          <SkeletonLoader v-if="isLoading && isMobile()" :height="'h-5'" class="mb-1 mt-2" rounded />
          <div v-else>
            <div v-if="isMobile()" class="flex mb-2 items-center space-x-3">
              <div class="flex-shrink-0">
                <div class="lg:flex justify-left items-center w-full flex-wrap">
                  <button
                    class="font-bold text-base uppercase text-university-secondary flex flex-row items-center cursor-pointer"
                    :class="[secondaryColor ? `${secondaryColorClass}` : `text-university-secondary`]"
                    @click="scrollToSection('dates')"
                  >
                    {{ viewMoreDeadlinesText }}
                    <ArrowRight custom-class="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <SkeletonLoader v-if="isLoading" :width="'w-96'" :height="'h-12'" rounded />
          <div v-else>
            <ApplyButton
              :id="selectedSession?.id"
              :disabled="pastSessionDeadline(selectedSession)"
              class="!p-5 text-md w-full h-16 mt-6 md:w-[313px]"
              type="button"
              event-category="Program Page Personalize CTA"
              show-arrow
              :external-link="externalLink"
              :register-type="registerType"
              @click="() => onApplyClicked({ program_page: programDetails, session: selectedSession })"
            />
          </div>
        </div>

        <div v-show="isLoading" class="w-full h-[20rem] mx-auto">
          <SkeletonLoader :height="'inherit'" class="mt-4" rounded />
        </div>
        <div class="bg-abstract-pattern flex w-full h-full md:pr-12 lg:pr-12">
          <!-- w-full/min-w-0 keep this flex item from collapsing around a video that has no intrinsic width. -->
          <div class="px-4 mt-4 w-full min-w-0 lg:col-start-2 lg:row-end-1">
            <!-- v-if, not v-show: the player measures its element on creation. -->
            <div v-if="leadVideo && !isLoading" class="w-full h-fit pb-8 mx-auto">
              <CloudinaryVideo :src="leadVideo" :label="videoLabel" :poster="image" />
            </div>
            <div v-else-if="image && !isLoading" class="w-full h-fit pb-6 mx-auto">
              <img class="w-full mx-auto" :src="image" :alt="mediaName" />
            </div>
          </div>
        </div>
      </div>

      <Ribbon :session="selectedSession" :is-loading="isLoading" :program-page="programDetails" />
      <div class="md:flex">
        <section
          ref="menuSection"
          class="bg-gray-10 w-full md:max-w-492p transition-height overflow-y-hidden md:overflow-y-visible"
          :style="{ height: inViewHeight }"
        >
          <div
            ref="menuInfoSection"
            id="side-bar-menu"
            class="md:block bg-gray-75 w-full md:max-w-492p md:min-w-492px scoped-sidebar-padding"
          >
            <ProgramDetailSideBar
              :session-id="displayedSessionId"
              :sessions="sessions"
              :is-loading="isLoading"
              :show-about="hasAboutText"
              :program-page="programDetails"
              :external-link="externalLink"
              :register-type="registerType"
              @apply-clicked="() => onApplyClicked({ program_page: programDetails, session: selectedSession })"
              @preview="closeModal"
              @select-session="(sessionId) => setSelectedSession(sessionId)"
              @set-menu-items="setActiveMenuItems"
            />
          </div>
          <div ref="menuItemsSection" id="sticky-sidebar-items" class="sticky top-0 self-start"></div>
        </section>
        <div class="min-w-0 flex-1">
          <div class="mt-6">
            <div>
              <AboutSection
                v-if="hasAboutText"
                id="about"
                :is-loading="isLoading"
                :description="programDetails?.description"
              />
                <ProgramDatesSection
                  id="dates"
                  :sessions="sessions"
                  :session-id="sessionId ?? ''"
                  :travel-details="selectedSession?.session_travel_details"
                  :is-loading="isLoading || isPlaceholderData"
                  :program-page="programDetails"
                  :external-link="externalLink"
                  :register-type="registerType"
                  @apply-clicked="() => onApplyClicked({ program_page: programDetails, session: selectedSession })"
                  @programdates-set-session-id="(sessionId) => setSelectedSession(sessionId)"
                  @preview="closeModal"
                />
              <StudentsSaying :quotes="getLearnerQuotes" />
              <LocationBlock id="location" :city="selectedSessionCity" @observe-section="observeSection" />
              <HostInstitutionBlock
                v-if="selectedSession.session_types && !isFacultyLed(selectedSession.session_types)"
                id="host"
                :host-institution="selectedSessionHostInstitution"
                @observe-section="observeSection"
              />
              <AcademicSection
                id="academicdetails"
                :session="selectedSession"
                :session-id="displayedSessionId"
                :host-institution="selectedSessionHostInstitution"
                :register-type="registerType"
                :external-link="externalLink"
                @apply-clicked="() => onApplyClicked({ program_page: programDetails, session: selectedSession })"
                @observe-section="observeSection"
              />
              <div v-show="selectedSession.session_units && selectedSession.session_units.length > 0">
                <div id="housing">
                  <HousingBlock
                    :session-id="displayedSessionId"
                    :sessions="sessions"
                    :external-link="externalLink"
                    :register-type="registerType"
                    @select-session="(sessionId) => setSelectedSession(sessionId)"
                    @personalize-and-apply="
                      () => onApplyClicked({ program_page: programDetails, session: selectedSession })
                    "
                    @observe-section="observeSection"
                    data-testid="program-details-housing-block"
                  />
                </div>
              </div>
              <TeamBlock id="team" :session-profiles="session_profiles" />
              <div id="internships">
                <InternshipSection
                  v-if="programDetails"
                  :internships-obj="selectedSessionInternships"
                  :session="selectedSession"
                  :program="programDetails"
                  :external-link="externalLink"
                  :register-type="registerType"
                  @apply-clicked="(internshipId) => onApplyClicked({ program_page: programDetails, session: selectedSession, internshipId })"
                  @observe-section="observeSection"
                />
              </div>
            </div>
          </div>
          <div id="excursions">
            <ExcursionBlock
              :session-id="displayedSessionId"
              :register-type="registerType"
              :external-link="externalLink"
              @personalize-and-apply="() => onApplyClicked({ program_page: programDetails, session: selectedSession })"
              @observe-section="observeSection"
            />
          </div>
          <div v-if="programDetails?.display_scholarship_opportunities" id="scholarship">
            <FinancialAid v-if="programDetails" :session="selectedSession" :program="programDetails" />
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <Page404 />
    </div>
  </div>
</template>

<style scoped>
.scoped-sidebar-padding {
  padding-top: 20px;
  padding-bottom: 20px;
}
</style>
